import { useMutation } from '@tanstack/react-query';
import { useContext, useEffect } from 'react';
import { Button, Container } from 'react-bootstrap';
import Card from 'react-bootstrap/Card';
import { BsArrowRight, BsCheckCircle } from 'react-icons/bs';
import { LinkContainer } from 'react-router-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';

import { localStore } from '../../v2/js/utils/storage';
import { AuthService, VerifyEmail as VerifyEmailModel } from '../api';
import { Logo } from '../app/components/Logo';
import { SessionContext } from '../app/contexts/SessionContext';
import { useDocumentTitle } from '../app/hooks/use-document-title';

export const VerifyEmail = () => {
  useDocumentTitle('Email Confirmation');
  const { key } = useParams();
  const navigate = useNavigate();
  const { setSessionExists } = useContext(SessionContext);

  const mutationVerifyEmail = useMutation({
    mutationFn: async (data: VerifyEmailModel) => {
      return AuthService.authRegistrationVerifyEmailCreate({
        requestBody: data,
      });
    },
    onSuccess: (user) => {
      // Confirming the link signs the user in, so go straight into the app
      // rather than asking for the password they just set. The endpoint
      // withholds the session (and returns no user) when it must not grant one
      // — an already-used or expired key — in which case fall through to the
      // "Continue to Login" card below.
      if (!user?.id) {
        return;
      }
      setSessionExists();
      const continueUrl = localStore.get('sessionContinueUrl');
      localStore.remove('sessionContinueUrl');
      navigate(continueUrl ? decodeURIComponent(continueUrl) : '/app');
    },
  });

  useEffect(() => {
    if (key) {
      mutationVerifyEmail.mutateAsync({ key });
    }
  }, [key]);

  const loadingState = (
    <div className="text-center">
      <h2 className="mb-4">Loading...</h2>
    </div>
  );

  const errorState = (
    <div className="text-center text-danger">
      <h2 className="mb-4">Something went wrong!</h2>
      <p>
        The verification link may be expired or your email may be confirmed already.
        <br />
        Please try logging in again.
      </p>
      <LinkContainer to="/login">
        <Button variant="primary" size="lg">
          Go to Login
        </Button>
      </LinkContainer>
    </div>
  );

  const successText = (
    <div className="text-center">
      <BsCheckCircle size="48px" className="mb-4 mx-auto color-primary" />
      <h2 className="mb-4">Your email is confirmed!</h2>
      <LinkContainer to="/login">
        <Button
          className="d-flex align-items-center justify-content-center"
          variant="primary"
          size="lg"
        >
          Continue to Login <BsArrowRight className="ms-2" />
        </Button>
      </LinkContainer>
    </div>
  );

  let currentState = loadingState;
  if (mutationVerifyEmail.isSuccess) currentState = successText;
  if (mutationVerifyEmail.isError) currentState = errorState;

  return (
    <div className="login-bg-container">
      <Container>
        <Card>
          <Card.Body>
            <div className="logo-container">
              <Logo className="logo" variant="color" />
            </div>
            {currentState}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};
